import { Box } from "@otrium/core";
import { useCallback, useEffect } from "react";
import { useRefinementList, useStats } from "react-instantsearch-core-v7";
import {
  FilterContentWrapper,
  ResultsButton,
  ScrollWrapper,
} from "src/organisms/AlgoliaProductList/DesktopFilterVeiw/DesktopFilter.styled";
import { BRAND_FACET } from "src/utils/algolia";
import {
  sortByCategoryName,
  sortByCount,
} from "src/utils/product-listing-helpers";
import { InputTextSearch } from "../InputTextSearch";
import { Radio } from "src/atoms/Radio";
import { Checkbox } from "src/atoms/Checkbox";
import { ColorFilterCheckbox } from "../ColorFilterCheckbox";
import {
  MobileClearAllButton,
  MobileFilterContainer,
} from "./MobileFilter.styled";
import { LabelNavigation } from "../LabelNavigation";
import { ArrowLeft } from "src/organisms/AlgoliaProductList/MobileFilterView/DrawerFilter/DrawerFilter.styled";
import { MessageDescriptor } from "@lingui/core";
import classNames from "classnames";
import { Trans } from "@lingui/macro";
import { theme } from "src/theme";
import { FilterType } from "src/organisms/MobileFilters";
import { useHandleSearchParams } from "src/hooks/use-handle-search-params";

type MobileFilterProps =
  | (
      | {
          title: MessageDescriptor;
          attribute: string;
          type: FilterType;
          isMultiColumn?: boolean;
          limit?: number;
          isOpen: boolean;
          hasFilters: boolean;
          toggleOpen: () => void;
          setCanRefine: (value: boolean) => void;
          onClear: () => void;
          handleScrollToTop: () => void;
        }
      | {
          title: MessageDescriptor;
          attribute: string;
          type: "range";
          isMultiColumn?: boolean;
          limit?: number;
          isOpen: boolean;
          hasFilters: boolean;
          toggleOpen: () => void;
          setCanRefine: (value: boolean) => void;
          onClear: () => void;
          handleScrollToTop: () => void;
        }
    ) &
      (
        | {
            showSearch: true;
            searchPlaceholder: string;
          }
        | { showSearch?: false; searchPlaceholder?: string }
      );

const multicolorIconLink =
  "https://colors-icons.s3.eu-central-1.amazonaws.com/multicoloured.png";

function MobileFilter({
  type,
  attribute,
  title,
  isMultiColumn,
  searchPlaceholder,
  showSearch,
  isOpen,
  hasFilters,
  toggleOpen,
  setCanRefine,
  onClear,
  handleScrollToTop,
}: MobileFilterProps): JSX.Element {
  const { nbHits: total } = useStats();
  const { items, refine, searchForItems, canRefine } = useRefinementList({
    attribute,
    sortBy: attribute === BRAND_FACET ? sortByCount : sortByCategoryName,
    limit: 200,
  });

  const { handleSearchParams } = useHandleSearchParams();

  const handleSingleSelect = useCallback(
    (value: string) => {
      const selectedItem = items.filter((item) => item.isRefined);

      handleSearchParams({
        facet: attribute,
        value,
        isSingle: true,
        deletePage: true,
      });

      if (selectedItem.length) {
        refine(selectedItem[0].value);
      }

      refine(value);

      handleScrollToTop();
    },
    [items, attribute, refine, handleSearchParams, handleScrollToTop]
  );

  const handleRefine = useCallback(
    (value: string) => {
      handleSearchParams({ facet: attribute, value, deletePage: true });

      refine(value);

      handleScrollToTop();
    },
    [attribute, refine, handleSearchParams, handleScrollToTop]
  );

  useEffect(() => {
    setCanRefine(canRefine);
  }, [canRefine, setCanRefine]);

  return (
    <MobileFilterContainer className={classNames({ ["open"]: isOpen })}>
      <LabelNavigation
        label={title}
        leftAdornment={
          <MobileClearAllButton className="no-padding" onClick={toggleOpen}>
            <ArrowLeft strokeWidth="2" />
          </MobileClearAllButton>
        }
        rightAdornment={
          hasFilters ? (
            <MobileClearAllButton onClick={onClear}>
              <Trans>Clear all</Trans>
            </MobileClearAllButton>
          ) : undefined
        }
      />
      <div className="content-wrapper">
        {showSearch ? (
          <Box paddingBlockEnd={3}>
            <InputTextSearch
              placeholder={searchPlaceholder}
              handleChange={(value) =>
                searchForItems(value.currentTarget.value)
              }
              onClearSearch={() => searchForItems("")}
            />
          </Box>
        ) : null}

        <ScrollWrapper>
          <FilterContentWrapper
            as={type === "color" ? "div" : "ul"}
            isMultiColumn={isMultiColumn && items.length > 1}
            count={Math.round(items.length / 2)}
          >
            {items.map((item) => {
              const splitLabel = item.label.split("||");
              const id = splitLabel[0];
              const label = splitLabel[1];

              if (type === "color") {
                const color = splitLabel[2];

                return (
                  <ColorFilterCheckbox
                    key={id}
                    id={id}
                    checked={item.isRefined}
                    onChange={() => handleRefine(item.value)}
                    icon={color ? undefined : multicolorIconLink}
                    name={label}
                    hex={color}
                  />
                );
              }

              if (type === "select") {
                return (
                  <Box
                    as="li"
                    key={`${attribute}-${id}`}
                    mb={theme.space.space24}
                  >
                    <Radio
                      id={id}
                      name={label}
                      checked={item.isRefined}
                      onChange={() => handleSingleSelect(item.value)}
                    />
                  </Box>
                );
              }

              return (
                <Box as="li" key={`${attribute}-${id}`}>
                  <Checkbox
                    checked={item.isRefined}
                    onChange={() => handleRefine(item.value)}
                  >
                    {label}
                  </Checkbox>
                </Box>
              );
            })}
          </FilterContentWrapper>
        </ScrollWrapper>

        <ResultsButton
          fullWidth
          variant="primary"
          onClick={toggleOpen}
          disabled={total <= 0}
          className="total-button"
        >
          <span>
            {total > 0 ? (
              <Trans>Show {total} results</Trans>
            ) : (
              <Trans>Close</Trans>
            )}
          </span>
        </ResultsButton>
      </div>
    </MobileFilterContainer>
  );
}

export { MobileFilter };
